import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth, AuthContext } from './AuthContext';
import { menuAPI } from '../services/api';

const PermissionContext = createContext();

// Store permissions in localStorage to prevent access denied on refresh
const LOCAL_STORAGE_KEY = 'glammify_permissions';
const LOCAL_STORAGE_MENU_KEY = 'glammify_menu';

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};

export const PermissionProvider = ({ children }) => {
  // Check if we're in a development environment and add some debugging
  const authContext = React.useContext(AuthContext);
  const { isAuthenticated, token } = authContext || { isAuthenticated: () => false, token: null };
  const [permissions, setPermissions] = useState(() => {
    // Initialize from localStorage if available
    try {
      const storedPermissions = localStorage.getItem(LOCAL_STORAGE_KEY);
      const parsedPermissions = storedPermissions ? JSON.parse(storedPermissions) : {};
      console.log('🔍 [PermissionContext] Initialized permissions from localStorage:', parsedPermissions);
      return parsedPermissions;
    } catch (error) {
      console.error('Error loading permissions from localStorage:', error);
      return {};
    }
  });
  
  const [menu, setMenu] = useState(() => {
    // Initialize menu from localStorage if available
    try {
      const storedMenu = localStorage.getItem(LOCAL_STORAGE_MENU_KEY);
      return storedMenu ? JSON.parse(storedMenu) : [];
    } catch (error) {
      console.error('Error loading menu from localStorage:', error);
      return [];
    }
  });
  
  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);

  useEffect(() => {
    if (isAuthenticated() && token) {
      // Check if we have cached permissions first
      const hasCachedPermissions = Object.keys(permissions).length > 0;
      if (hasCachedPermissions) {
        console.log('🔍 [PermissionContext] Using cached permissions, setting initialized to true');
        setInitialized(true);
      }
      
      // Add a small delay to ensure token is properly set in API headers
      const timer = setTimeout(() => {
        loadPermissions();
      }, 100);
      return () => clearTimeout(timer);
    } else {
      setPermissions({});
      setMenu([]);
      setInitialized(false);
      // Clear localStorage when logged out
      localStorage.removeItem(LOCAL_STORAGE_KEY);
      localStorage.removeItem(LOCAL_STORAGE_MENU_KEY);
    }
  }, [token]);

  // Add a fallback initialization effect
  useEffect(() => {
    if (isAuthenticated() && token && !initialized && !loading) {
      // If we're authenticated but not initialized and not loading, try to initialize
      const hasCachedPermissions = Object.keys(permissions).length > 0;
      if (hasCachedPermissions) {
        console.log('🔍 [PermissionContext] Fallback: Using cached permissions');
        setInitialized(true);
      } else {
        // Force load permissions if we don't have any
        console.log('🔍 [PermissionContext] Fallback: Force loading permissions');
        loadPermissions();
      }
    }
  }, [isAuthenticated, token, initialized, loading, permissions]);

  // Save permissions to localStorage whenever they change
  useEffect(() => {
    if (Object.keys(permissions).length > 0) {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(permissions));
    }
  }, [permissions]);

  // Save menu to localStorage whenever it changes
  useEffect(() => {
    if (menu.length > 0) {
      localStorage.setItem(LOCAL_STORAGE_MENU_KEY, JSON.stringify(menu));
    }
  }, [menu]);

  const loadPermissions = useCallback(async (isAutoRefresh = false) => {
    if (!isAutoRefresh) {
      console.log('🔍 [PermissionContext] Loading permissions...');
    }
    setLoading(true);
    try {
      // Load sidebar menu with permissions
      const menuResponse = await menuAPI.getSidebar();
      if (!isAutoRefresh) {
        console.log('✅ [PermissionContext] Menu API response:', menuResponse.data);
      }
      
      const newMenu = menuResponse.data.menu || [];
      
      // Convert menu to permissions object for easy checking
      const newPermissionsObj = {};
      newMenu.forEach(module => {
        newPermissionsObj[module.module] = {};
        (module.actions || []).forEach(action => {
          newPermissionsObj[module.module][action.name] = true;
        });
      });
      
      // Check if permissions actually changed (for auto-refresh)
      if (isAutoRefresh) {
        const hasChanged = JSON.stringify(newPermissionsObj) !== JSON.stringify(permissions);
        if (hasChanged) {
          console.log('🔄 [PermissionContext] Permissions changed detected - updating silently...');
        } else {
          setLoading(false);
          return; // No changes, don't update
        }
      }
      
      if (!isAutoRefresh) {
        console.log('📊 [PermissionContext] Converted permissions:', newPermissionsObj);
        
        // Check Users specifically
        if (newPermissionsObj.Users) {
          console.log('👥 [PermissionContext] Users permissions:', newPermissionsObj.Users);
          console.log('👥 [PermissionContext] Users.view:', !!newPermissionsObj.Users.view);
          console.log('👥 [PermissionContext] Users.create:', !!newPermissionsObj.Users.create);
        } else {
          console.log('❌ [PermissionContext] Users module not found in permissions');
        }
      }
      
      setMenu(newMenu);
      setPermissions(newPermissionsObj);
      setLastRefresh(new Date().toISOString());
      setInitialized(true);
      
    } catch (error) {
      console.error('❌ [PermissionContext] Error loading permissions:', error);
      // Don't clear permissions on error to avoid redirect loops
      // Only clear if it's not a 401 error (which would be handled by interceptor)
      if (error.response && error.response.status !== 401) {
        // Don't clear permissions on error - use cached permissions instead
        console.log('⚠️ [PermissionContext] Using cached permissions due to error');
      }
    } finally {
      setLoading(false);
    }
  }, [permissions]);

  // Add this function to handle permission refresh without reloading
  const refreshPermissions = async () => {
    console.log('🔄 [PermissionContext] Refreshing permissions...');
    try {
      await loadPermissions();
      return true; // Return success status
    } catch (error) {
      console.error('❌ [PermissionContext] Error refreshing permissions:', error);
      return false; // Return failure status
    }
  };

  // Force refresh function for debugging
  const forceRefreshPermissions = async () => {
    console.log('🔄 [PermissionContext] Force refreshing permissions...');
    try {
      await loadPermissions();
      return true; // Return success status
    } catch (error) {
      console.error('❌ [PermissionContext] Error force refreshing permissions:', error);
      return false; // Return failure status
    }
  };

  // Add to window for debugging
  if (typeof window !== 'undefined') {
    window.forceRefreshPermissions = forceRefreshPermissions;
    window.refreshPermissions = refreshPermissions;
  }

  // Add keyboard shortcut for manual refresh (Ctrl+Shift+P)
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.ctrlKey && event.shiftKey && event.key === 'P') {
        event.preventDefault();
        console.log('🔄 [PermissionContext] Manual refresh triggered via Ctrl+Shift+P');
        refreshPermissions();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  // Auto-refresh permissions every 2 minutes for other users' changes
  useEffect(() => {
    if (!isAuthenticated() || !autoRefreshEnabled) return;

    const interval = setInterval(() => {
      loadPermissions(true); // Pass true for auto-refresh
    }, 2 * 60 * 1000); // 2 minutes

    return () => clearInterval(interval);
  }, [autoRefreshEnabled, loadPermissions]);

  const hasPermission = (module, action) => {
    const result = permissions[module] && permissions[module][action] === true;
    // Log permission checks for debugging
    if (window.DEBUG_PERMISSIONS || (!result && (module === 'Users' || module === 'Dashboard' || module === 'MerchantManagement'))) {
      console.log(`🔍 [PermissionContext] hasPermission(${module}, ${action}):`, result);
      console.log(`🔍 [PermissionContext] permissions[${module}]:`, permissions[module]);
      console.log(`🔍 [PermissionContext] initialized:`, initialized);
      console.log(`🔍 [PermissionContext] loading:`, loading);
    }
    return result;
  };

  const hasAnyPermission = (module) => {
    return permissions[module] && Object.keys(permissions[module]).length > 0;
  };

  const getModuleActions = (module) => {
    const menuItem = menu.find(item => item.module === module);
    return (menuItem && menuItem.actions) || [];
  };

  const toggleAutoRefresh = () => {
    setAutoRefreshEnabled(prev => !prev);
    console.log(`🔄 [PermissionContext] Auto-refresh ${!autoRefreshEnabled ? 'enabled' : 'disabled'}`);
  };

  const value = {
    permissions,
    menu,
    loading,
    initialized,
    lastRefresh,
    autoRefreshEnabled,
    hasPermission,
    hasAnyPermission,
    getModuleActions,
    loadPermissions,
    refreshPermissions,
    forceRefreshPermissions,
    toggleAutoRefresh
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
}; 